/**
 * /dashboard/me/profile — "My Profile" (UX-21, employee self-service).
 *
 * A READ-ONLY view of the caller's own personal + employment details,
 * served by GET /api/v1/me/profile (self-scoped; any authed user). There
 * is no edit affordance here on purpose — corrections flow through the
 * existing change-requests workflow, linked from the footer note.
 *
 * Layout: five Card sections (Personal / Employment / Emergency contact /
 * Nominee / Address), each a simple label/value definition list. Absent
 * fields (the backend omits NULL keys) render a muted em-dash so the page
 * shape stays stable for sparse profiles. Skeleton while loading; inline
 * Alert on error. Lives in the "Me" module strip (lib/me-nav.ts).
 */

import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  IdCard,
  MapPin,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toApiError } from "@/lib/api";
import { api } from "@/lib/api/client";
import { formatDate } from "@/lib/format";
import { formatChangeRequestValue } from "@/lib/change-request-display";
import { useMyProfile } from "@/hooks/use-me-profile";
import type { MeProfile } from "@/types/me-profile";
import { MeModuleShell } from "@/components/shared/me-module-shell";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DottedLinesCard } from "@/components/org-chart/dotted-lines-card";
import { ContactQuickEdit } from "@/components/profile/contact-quick-edit";
import { SignInEmailCard } from "@/components/profile/sign-in-email-card";
import { StatutoryFormsCard } from "@/components/people/statutory-forms-card";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/me/profile",
)({
  component: MyProfileRoute,
});

// ---------------------------------------------------------------------------
// Field formatting helpers.
// ---------------------------------------------------------------------------

/** Render an absent / blank value as a muted em-dash. */
const EMPTY = "—";

function fmtText(v?: string): string {
  const t = v?.trim();
  return t ? t : EMPTY;
}

/** YYYY-MM-DD → "1 Jul 2026" (lib/format; was the browser locale's
 *  "July 1, 2026"); falls back to the raw string, then EMPTY. */
function fmtDate(v?: string): string {
  return formatDate(v, EMPTY);
}

/** Title-case a snake/lower enum like "full_time" → "Full time". */
function fmtEnum(v?: string): string {
  const t = v?.trim();
  if (!t) return EMPTY;
  const spaced = t.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------------------------------------------------------------------------
// Definition-list section.
// ---------------------------------------------------------------------------

interface Field {
  label: string;
  value: string;
}

interface GrievanceOfficer {
  name: string | null;
  email: string | null;
  phone: string | null;
  designated: boolean;
}

/**
 * DPDP Act 2023 §13 — every employee must be able to find the contact
 * for data-protection grievances. Reads the member-visible endpoint
 * (no role gate); shows a "not designated" state until HR names one.
 */
function DataPrivacyCard() {
  const q = useQuery({
    queryKey: ["tenant", "grievance-officer"],
    queryFn: async () => {
      const { data } = await api.get<GrievanceOfficer>(
        "/api/v1/tenant/grievance-officer",
      );
      return data;
    },
    staleTime: 60 * 60 * 1000, // changes rarely
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck aria-hidden className="h-4 w-4 text-muted-foreground" />
          Data Privacy
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {q.data?.designated ? (
          <p>
            Your employer&apos;s data-protection grievance officer
            {q.data.name ? (
              <>
                {" "}is <span className="font-medium">{q.data.name}</span>
              </>
            ) : null}
            {q.data.email ? (
              <>
                {" — "}
                <a
                  href={`mailto:${q.data.email}`}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  {q.data.email}
                </a>
              </>
            ) : null}
            {q.data.phone ? <> ({q.data.phone})</> : null}
            . Contact them with any concern about how your personal data
            is handled (DPDP Act 2023).
          </p>
        ) : (
          <p className="text-muted-foreground">
            Your employer hasn&apos;t designated a data-protection
            grievance officer yet. For data-privacy concerns, contact
            your HR department.
          </p>
        )}
        <p className="text-muted-foreground">
          How your data is processed on this platform:{" "}
          <a
            href="https://workright.in/privacy-employees"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            employee privacy notice
          </a>
          .
        </p>
      </CardContent>
    </Card>
  );
}

function ProfileSection({
  icon: Icon,
  title,
  fields,
}: Readonly<{ icon: LucideIcon; title: string; fields: Field[] }>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon aria-hidden className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.label} className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {f.label}
              </dt>
              <dd className="mt-0.5 break-words text-sm text-foreground">
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1, 2].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              {[0, 1, 2, 3].map((j) => (
                <div key={j} className="space-y-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-4 w-36" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function buildSections(p: MeProfile): {
  icon: LucideIcon;
  title: string;
  fields: Field[];
}[] {
  const fullName = [p.first_name, p.last_name]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(" ");
  return [
    {
      icon: UserRound,
      title: "Personal",
      fields: [
        { label: "Name", value: fullName || EMPTY },
        { label: "Date of Birth", value: fmtDate(p.date_of_birth) },
        { label: "Gender", value: fmtEnum(p.gender) },
        { label: "Phone", value: fmtText(p.phone) },
        { label: "Email", value: fmtText(p.email) },
      ],
    },
    {
      icon: Briefcase,
      title: "Employment",
      fields: [
        { label: "Employee ID", value: fmtText(p.employee_id) },
        { label: "Department", value: fmtText(p.department) },
        { label: "Designation", value: fmtText(p.designation) },
        { label: "Manager", value: fmtText(p.manager_name) },
        { label: "Date of Joining", value: fmtDate(p.hire_date) },
        { label: "Employment Type", value: fmtEnum(p.employment_type) },
        { label: "Location", value: fmtText(p.location) },
      ],
    },
    {
      icon: Phone,
      title: "Emergency Contact",
      fields: [
        { label: "Name", value: fmtText(p.emergency_contact_name) },
        { label: "Phone", value: fmtText(p.emergency_contact_phone) },
        { label: "Relationship", value: fmtText(p.emergency_contact_relation) },
      ],
    },
    {
      icon: IdCard,
      title: "Nominee",
      fields: [
        { label: "Name", value: fmtText(p.nominee_name) },
        // The stored enum ("parent") read as a word — the same label the
        // change-request form and comparison use.
        { label: "Relationship", value: formatChangeRequestValue("nominee_relation", p.nominee_relation) },
        { label: "Date of Birth", value: fmtDate(p.nominee_dob) },
      ],
    },
    {
      icon: MapPin,
      title: "Address",
      fields: [
        { label: "Current Address", value: fmtText(p.address_current) },
        { label: "Permanent Address", value: fmtText(p.address_permanent) },
      ],
    },
  ];
}

function MyProfileRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const q = useMyProfile();
  const { user } = useAuth();

  return (
    <MeModuleShell
      title="Details"
      description="Your personal and employment details."
    >
      {q.isLoading && <ProfileSkeleton />}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load your profile</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {!q.isLoading && !q.isError && q.data && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <ContactQuickEdit profile={q.data} />
          </div>
          {buildSections(q.data).map((s) => (
            <ProfileSection
              key={s.title}
              icon={s.icon}
              title={s.title}
              fields={s.fields}
            />
          ))}

          {/* Self-service sign-in email change (people QA #5). */}
          {q.data.email && (
            <SignInEmailCard email={q.data.email} emailConfigured={q.data.email_configured} />
          )}

          {/* Matrix (dotted) reporting — informational; renders only when set. */}
          <DottedLinesCard
            managers={q.data.dotted_managers}
            reports={q.data.dotted_reports}
            selfView
          />

          {/* Wave B: own Form 11 / Form F (view, fill in, print). */}
          {user?.user_id ? <StatutoryFormsCard employeeId={user.user_id} self /> : null}

          <DataPrivacyCard />

          <p className="text-sm text-muted-foreground">
            Phone and emergency contact you can edit above. To correct any
            other detail — name, date of birth, nominee, address, bank
            account — {" "}
            <Link
              to="/app/$tenantSlug/change-requests/new"
              params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              raise a change request
            </Link>
            .
          </p>
        </div>
      )}
    </MeModuleShell>
  );
}
